/**
 * Adapts the GraphQL catalog responses (Query.chains + Query.tokens) to the
 * `ChainDef[]` shape that `QuoteForm` consumes.
 *
 * Design constraint: aTokens (LOCAL_ATOKENS_BY_CHAIN) are NOT mergeable here.
 * The select must reflect EXACTLY what `policy.allowedTokens` authorizes —
 * aTokens are Polygon receipt tokens that LI.FI does not list, and they only
 * appear in the form when an Aave preset auto-fills `fromToken` (handled by
 * `QuoteForm`'s preset effect, not this merge).
 *
 * The native gas token is taken from `chain.nativeToken` (which the API
 * provides per chain). Absent means the policy filtered it out — we
 * propagate that as a null entry and the form never lists it.
 */

import { type ChainDef, type TokenDef } from "../data/tokens.js";

interface CatalogNativeToken {
  readonly chainId: number;
  readonly address: `0x${string}`;
  readonly symbol: string;
  readonly decimals: number;
  readonly name: string;
  readonly logoURI?: string | null;
  readonly priceUSD?: string | null;
}

interface CatalogChain {
  readonly id: number;
  readonly key: string;
  readonly name: string;
  readonly coin: string;
  readonly mainnet: boolean;
  readonly logoURI?: string | null;
  readonly allowBefore?: boolean;
  readonly nativeToken?: CatalogNativeToken | null;
}

interface CatalogToken {
  readonly chainId: number;
  readonly address: `0x${string}`;
  readonly symbol: string;
  readonly decimals: number;
  readonly name: string;
  readonly logoURI?: string | null;
  readonly priceUSD?: string | null;
}

/**
 * Top-level shapes returned by the API for the catalog queries.
 * Exported so `useCatalog` can type its `gqlFetch<T>` calls against the
 * SAME interfaces this function consumes — no risk of structural drift.
 */
export interface ChainsResponseData {
  readonly chains?: {
    readonly __typename?: string;
    readonly chains?: readonly CatalogChain[];
  };
}

export interface TokensResponseData {
  readonly tokens?: {
    readonly __typename?: string;
    readonly byChain?: ReadonlyArray<{
      readonly chainId: number;
      readonly tokens: readonly CatalogToken[];
    }>;
  };
}

export function mergeCatalog(
  chainsData: ChainsResponseData | undefined,
  tokensData: TokensResponseData | undefined,
): readonly ChainDef[] {
  const chains = chainsData?.chains?.chains ?? [];
  const tokensByChain = new Map<number, readonly CatalogToken[]>(
    (tokensData?.tokens?.byChain ?? []).map((g) => [g.chainId, g.tokens]),
  );

  return chains.map((c): ChainDef => {
    const apiTokens = tokensByChain.get(c.id) ?? [];
    // The API omits `nativeToken` when the integrator's policy does not
    // authorise the native sentinel for this chain. Respect that —
    // surfacing the native here would mislead the user into picking a
    // token that `buildTx` would later reject with UNSUPPORTED_TOKEN.
    const nativeSymbol = c.nativeToken?.symbol ?? c.coin ?? "ETH";
    const nativeTokenDef: TokenDef | null = c.nativeToken
      ? {
          symbol: c.nativeToken.symbol,
          name: c.nativeToken.name,
          address: c.nativeToken.address,
          decimals: c.nativeToken.decimals,
          isNative: true,
          ...(c.nativeToken.logoURI != null
            ? { logoURI: c.nativeToken.logoURI }
            : {}),
          ...(c.nativeToken.priceUSD != null
            ? { priceUSD: c.nativeToken.priceUSD }
            : {}),
        }
      : null;

    const tokens: readonly TokenDef[] = [
      // 1. Native gas token, only when the API authorises it for this tenant.
      ...(nativeTokenDef !== null ? [nativeTokenDef] : []),
      // 2. ERC20s del catálogo, filtrados por la policy del API key activa.
      ...apiTokens.map(
        (t): TokenDef => ({
          symbol: t.symbol,
          name: t.name,
          address: t.address,
          decimals: t.decimals,
          ...(t.logoURI != null ? { logoURI: t.logoURI } : {}),
          ...(t.priceUSD != null ? { priceUSD: t.priceUSD } : {}),
        }),
      ),
    ];

    return {
      id: c.id,
      name: c.name,
      shortName: c.key,
      nativeSymbol,
      tokens,
      mainnet: c.mainnet,
      ...(c.logoURI != null ? { logoURI: c.logoURI } : {}),
      ...(c.allowBefore != null ? { allowBefore: c.allowBefore } : {}),
    };
  });
}
